import {
  Alert,
  Badge,
  Button,
  Group,
  List,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { modals } from '@mantine/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { CrudSection } from '../components/CrudSection';
import { describeEligibility } from '../components/EligibilityField';
import { EntityLabel, EntitySelect } from '../components/EntityField';
import { describeSchedule } from '../components/ScheduleField';
import { useEntityIndex } from '../hooks/useEntityIndex';
import { useLocationId } from '../hooks/useSeedLocation';
import { useTabInUrl } from '../hooks/useTabInUrl';
import { enumLabel, enumOptions, enumOptionsFor } from '../lib/enums';
import { formatFxRate, formatMoney, formatRateBp } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';
import { LockedLempiraPrices } from './pricing/LockedLempiraPrices';
import { StaffMealEligible } from './StaffMealEligible';

/** The three things a price can be attached to. A dish is priced through its sizes, not directly. */
const PRICED_TARGETS = ['variant', 'modifierOption', 'bundle'] as const;

/**
 * What one price is for, in a single cell.
 *
 * The kind and the thing were two columns saying "Size" beside "Salva Vida — Pitcher", which is
 * one sentence split in half and 150 pixels of table spent on the half nobody reads. Everything a
 * price can hang off is listable by name, so the name leads and the kind sits under it.
 */
function PriceTarget({ row }: { row: Record<string, unknown> }) {
  const kind = enumLabel('priceTargetType', row.targetType);
  const entity =
    row.targetType === 'variant'
      ? ('variant' as const)
      : row.targetType === 'modifierOption'
        ? ('modifierOption' as const)
        : row.targetType === 'bundle'
          ? ('bundle' as const)
          : row.targetType === 'menuItem'
            ? ('menuItem' as const)
            : row.targetType === 'category'
              ? ('category' as const)
              : null;
  // Only reached by a price written against something this build cannot name; the kind alone still
  // says more than the stored reference would.
  if (!entity) return <Text c="dimmed">{kind}</Text>;
  return (
    <div>
      <EntityLabel entity={entity} id={row.targetId} />
      <Text size="xs" c="dimmed">
        {kind}
      </Text>
    </div>
  );
}

/**
 * Taking a price off a list.
 *
 * A price sitting on the wrong drink is money charged wrongly at the register, and the only repair used
 * to be editing it into something else — there was no way to say "this should not be priced here at
 * all", so a happy-hour list would keep a line somebody added by mistake for as long as it existed.
 */
function RemovePrice({ row, refresh }: { row: Record<string, unknown>; refresh: () => void }) {
  const remove = useMutation({
    mutationFn: () => rawApi('DELETE', `/admin/v1/pricing/price-entries/${String(row.id)}`),
    onSuccess: () => {
      refresh();
      notifyDone('Price removed', 'This list no longer prices that.');
    },
    onError: (err) => notifyError(err, 'remove this price'),
  });

  return (
    <Button
      size="xs"
      variant="subtle"
      color="red"
      loading={remove.isPending}
      onClick={() =>
        modals.openConfirmModal({
          title: 'Remove this price?',
          children: (
            <Text size="sm">
              The item stays on the menu — it simply has no price on this list any more. If no other list
              covers it, staff cannot ring it up until you price it again.
            </Text>
          ),
          labels: { confirm: 'Remove it', cancel: 'Keep it' },
          confirmProps: { color: 'red' },
          onConfirm: () => remove.mutate(),
        })
      }
    >
      Remove
    </Button>
  );
}

/** "2+ at L 90 each; 6+ at L 500 the lot" — the deal itself, not the fact that one exists. */
function describeQtyTiers(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) return '';
  return (value as Record<string, unknown>[])
    .map((t) => {
      const amount =
        typeof t.hnlMinor === 'number'
          ? formatMoney(t.hnlMinor, 'HNL')
          : typeof t.usdMinor === 'number'
            ? formatMoney(t.usdMinor, 'USD')
            : '';
      const how = t.mode === 'totalForQty' ? 'the lot' : 'each';
      return `${t.minQty ?? '?'}+ at ${amount} ${how}`;
    })
    .join('; ');
}

/** Mantine's date-and-time value, in local wall-clock parts: "2026-07-25 17:30:00". */
function nowStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

function toInstant(stamp: string | null): string {
  if (!stamp) return new Date().toISOString();
  const parsed = new Date(stamp.replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
}

interface ResolvedPrice {
  currency: 'USD' | 'HNL';
  unitPriceMinor: number;
  lineTotalMinor: number;
  priceListId: string;
  taxInclusive: boolean;
  appliedRules: { kind: string; detail: string }[];
}

function PriceCheck() {
  const [targetType, setTargetType] = useState<string | null>('variant');
  const [targetId, setTargetId] = useState('');
  // Defaults to right now. A fixed instant meant the answer stopped matching the bar within a day.
  const [at, setAt] = useState<string | null>(nowStamp());
  const [currency, setCurrency] = useState<string | null>('USD');
  const [qty, setQty] = useState<number | string>(1);

  const check = useMutation({
    mutationFn: () =>
      rawApi<ResolvedPrice>('POST', '/admin/v1/pricing/resolve-preview', {
        targetType: targetType ?? 'variant',
        targetId: targetId.trim(),
        qty: Number(qty) || 1,
        at: toInstant(at),
        pricingCurrency: currency ?? 'USD',
      }),
    onError: (err) => notifyError(err, 'work out this price'),
  });

  const result = check.data;
  const count = Number(qty) || 1;

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5} mb="xs">
        Check a price
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        What a guest would be charged at any date and time, worked out exactly as the registers do it. Use it
        to confirm a happy hour or a weekend special before the night starts.
      </Text>
      <Group align="flex-end" gap="sm" wrap="wrap">
        <Select
          label="What you are pricing"
          data={enumOptionsFor('priceTargetType', PRICED_TARGETS)}
          value={targetType}
          onChange={setTargetType}
          allowDeselect={false}
          w={200}
          comboboxProps={{ withinPortal: true }}
        />
        {/* "Check a price" is the tool for confirming happy hour before the night starts, and it
            used to need an identifier pasted in from somewhere else — so the answer it gives was
            only ever available to someone who already knew where to look. */}
        <div style={{ width: 320 }}>
          <EntitySelect
            entity={
              targetType === 'modifierOption'
                ? 'modifierOption'
                : targetType === 'bundle'
                  ? 'bundle'
                  : 'variant'
            }
            label="Which one"
            value={targetId || null}
            onChange={(v: string | null) => setTargetId(v ?? '')}
          />
        </div>
        <DateTimePicker
          label="Date and time"
          value={at}
          onChange={setAt}
          valueFormat="D MMM YYYY, h:mm A"
          w={230}
          popoverProps={{ withinPortal: true }}
        />
        <Select
          label="Priced in"
          data={enumOptions('currency')}
          value={currency}
          onChange={setCurrency}
          allowDeselect={false}
          w={170}
          comboboxProps={{ withinPortal: true }}
        />
        <NumberInput label="How many" value={qty} onChange={setQty} w={110} min={1} />
        <Button onClick={() => check.mutate()} loading={check.isPending} disabled={!targetId.trim()}>
          Check price
        </Button>
      </Group>

      {result && (
        <Stack gap={6} mt="lg">
          <Group gap="sm" align="baseline">
            <Text fw={700} fz="xl">
              {formatMoney(result.lineTotalMinor, result.currency)}
            </Text>
            {count > 1 && (
              <Text size="sm" c="dimmed">
                {formatMoney(result.unitPriceMinor, result.currency)} each
              </Text>
            )}
          </Group>
          <Group gap={6}>
            <Text size="sm" c="dimmed">
              From
            </Text>
            <EntityLabel entity="priceList" id={result.priceListId} />
          </Group>
          <Text size="sm" c="dimmed">
            {result.taxInclusive
              ? 'Tax is already included in this price.'
              : 'Tax is added on top of this price.'}
          </Text>
          {result.appliedRules.length > 0 && (
            <>
              <Text size="sm" fw={600} mt={4}>
                Why this price
              </Text>
              {/* The engine's own `detail` strings are left out on purpose: they carry stored
                  references that mean nothing to anyone reading this screen. */}
              <List size="sm" spacing={2}>
                {result.appliedRules.map((rule) => (
                  <List.Item key={`${rule.kind}-${rule.detail}`}>
                    {enumLabel('priceRuleKind', rule.kind)}
                  </List.Item>
                ))}
              </List>
            </>
          )}
        </Stack>
      )}
    </Paper>
  );
}

/**
 * Prices live inside a chosen list. This used to be pinned to the everyday list, which left Happy
 * Hour, Monday and Taco Tuesday with no way in — the most routine change this bar makes.
 *
 * Which list is being read is in the address, so the row on the Price lists tab can open the
 * prices ON that list rather than leaving the manager to re-find "Happy Hour" in a dropdown they
 * had just been looking at by name.
 */
function PriceEntries({
  chosen,
  onChoose,
}: {
  chosen: string | null;
  onChoose: (id: string | null) => void;
}) {
  const lists = useEntityIndex('priceList');
  const listId = chosen ?? lists.options[0]?.value ?? null;
  const listName = lists.options.find((o) => o.value === listId)?.label;

  if (lists.isError) {
    return (
      <Alert color="red" title="Could not load your price lists">
        Nothing has been lost — this is only a display problem. Reload the page to try again.
      </Alert>
    );
  }
  if (!listId) {
    return lists.isLoading ? (
      <Text c="dimmed">Loading your price lists…</Text>
    ) : (
      <Alert color="blue" title="No price lists yet">
        Add a price list on the previous tab first, then you can put prices on it.
      </Alert>
    );
  }

  return (
    <CrudSection
      title={listName ? `Prices on ${listName}` : 'Prices'}
      entityNoun="price"
      description="Set a dollar price, a lempira price, or both — each stands on its own rather than being converted at the register. Deals like two-for-100 go here too."
      listPath={`/admin/v1/pricing/price-lists/${listId}/entries`}
      createPath="/admin/v1/pricing/price-entries"
      updatePathTemplate="/admin/v1/pricing/price-entries/{id}"
      defaults={{ priceListId: listId }}
      emptyMessage="Nothing priced on this list yet. Add a price, or switch to another list above."
      actions={
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" c="dimmed">
            Price list
          </Text>
          <Select
            aria-label="Price list"
            data={lists.options.map((o) => ({ value: o.value, label: o.label }))}
            value={listId}
            onChange={onChoose}
            allowDeselect={false}
            searchable={lists.options.length > 8}
            w={220}
            comboboxProps={{ withinPortal: true }}
          />
        </Group>
      }
      rowActions={(row, refresh) => <RemovePrice row={row} refresh={refresh} />}
      columns={[
        {
          accessor: 'targetId',
          title: 'What is priced',
          render: (r) => <PriceTarget row={r} />,
        },
        // Each amount column is one currency by definition, so the code is fixed rather than read
        // off the row — a price entry has no single currency of its own.
        {
          accessor: 'usdMinor',
          title: 'Dollars',
          render: (r) => formatMoney(r.usdMinor as number, 'USD'),
        },
        {
          accessor: 'hnlMinor',
          title: 'Lempiras',
          render: (r) => formatMoney(r.hnlMinor as number, 'HNL'),
        },
        { accessor: 'adjustmentBp', title: 'Adjustment', unit: 'percent' },
        {
          accessor: 'qtyTiers',
          title: 'Quantity deals',
          // "Yes" told a manager a deal exists without telling them what it is, which is the one
          // thing they opened the table to find out.
          render: (r) => describeQtyTiers(r.qtyTiers),
        },
      ]}
      fields={[
        {
          name: 'targetType',
          label: 'What you are pricing',
          type: 'select',
          required: true,
          options: enumOptionsFor('priceTargetType', PRICED_TARGETS),
        },
        {
          name: 'targetId',
          label: 'Which one',
          type: 'entity',
          required: true,
          resource: (values) => {
            if (values.targetType === 'variant') return 'variant';
            if (values.targetType === 'modifierOption') return 'modifierOption';
            if (values.targetType === 'bundle') return 'bundle';
            return undefined;
          },
        },
        {
          name: 'usdMinor',
          label: 'Dollar price',
          type: 'number',
          unit: 'money',
          nullable: true,
          description: 'Leave empty if this is not sold in dollars',
          // The server refuses an entry with no amount at all; catching it here keeps the message
          // on the form instead of arriving as a red toast after the save.
          validate: (_value, values) =>
            ['usdMinor', 'hnlMinor', 'adjustmentBp', 'qtyTiers'].some(
              (k) => values[k] !== '' && values[k] !== null && values[k] !== undefined,
            )
              ? null
              : 'Enter a dollar price, a lempira price, or a percentage adjustment',
        },
        {
          name: 'hnlMinor',
          label: 'Lempira price',
          type: 'number',
          unit: 'money',
          nullable: true,
          /*
           * THE QUESTION THIS FIELD ASKS IS NOT "is this sold in lempiras" — everything is. Asked
           * that way, the honest answer is yes, and the person answering has just created a
           * permanent lempira price without meaning to. Six beers on the everyday list carried L50
           * and L100 that way. It asks about LOCKING, which is what it does.
           */
          description:
            'Leave it empty and the lempira figure follows the exchange rate. Type one to lock it, ' +
            'and it stops following the rate until you clear this box again.',
        },
        {
          name: 'adjustmentBp',
          label: 'Percentage adjustment',
          type: 'number',
          unit: 'percent',
          nullable: true,
          description:
            'Instead of an amount: how far off the everyday price this is. Use a minus for a discount.',
        },
        {
          name: 'qtyTiers',
          label: 'Quantity deals',
          type: 'rows',
          nullable: true,
          rowNoun: 'deal',
          description:
            'For offers like two for L 100. Leave empty when the price is the same at any quantity.',
          rowFields: [
            {
              name: 'minQty',
              label: 'From this many',
              type: 'number',
              description: 'Two for the price of one starts at 2',
            },
            {
              name: 'mode',
              label: 'The price below is',
              type: 'select',
              defaultValue: 'perUnit',
              // This is the difference between L 100 a drink and L 100 for the round, and the
              // stored words say neither. It is the field most likely to be got wrong, so it says
              // what it means rather than naming the enum.
              options: [
                { value: 'perUnit', label: 'Each' },
                { value: 'totalForQty', label: 'For all of them together' },
              ],
            },
            { name: 'hnlMinor', label: 'Lempiras', type: 'number', unit: 'money' },
            { name: 'usdMinor', label: 'Dollars', type: 'number', unit: 'money' },
          ],
        },
      ]}
    />
  );
}

const ROUNDING_PATH = '/admin/v1/pricing/rounding-policies';

interface RoundingPolicy {
  id: string;
  locationId: string;
  currency: string;
  incrementMinor: number;
  direction: string;
  changeRule: string;
}

/**
 * How a cash total is rounded, which the screen could describe and not set.
 *
 * No coins circulate on Roatán, so every cash check has to land on a whole note — and the rule
 * saying so was readable and frozen. A new register, a change of mind about rounding up rather than to
 * the nearest, or the day dollars start being taken in cash, all needed a database console. The
 * empty state was the worst of it: "No rounding set up yet" and no way to set it up.
 */
function CashRounding() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<RoundingPolicy | 'new' | null>(null);

  const policies = useQuery({
    queryKey: listKey(ROUNDING_PATH),
    queryFn: () => rawApi<RoundingPolicy[]>('GET', ROUNDING_PATH),
  });

  const taken = new Set((policies.data ?? []).map((p) => p.currency));
  const spare = enumOptions('currency').filter((o) => !taken.has(o.value));

  return (
    <>
      <CrudSection
        title="Cash rounding"
        description="A cash total is rounded once, at the end of the check, because no coins circulate here. Card and transfer payments are settled to the cent."
        listPath={ROUNDING_PATH}
        emptyMessage="No rounding set up yet, so cash totals come out to the cent — add a rule for the money you take."
        actions={
          spare.length > 0 ? (
            <Button size="sm" onClick={() => setEditing('new')} disabled={!locationId}>
              Set up rounding
            </Button>
          ) : null
        }
        rowActions={(row) => (
          <Button size="xs" variant="light" onClick={() => setEditing(row as unknown as RoundingPolicy)}>
            Change
          </Button>
        )}
        columns={[
          { accessor: 'currency', title: 'Money', enumGroup: 'currency' },
          {
            accessor: 'incrementMinor',
            title: 'Rounds to',
            unit: 'money',
            currencyAccessor: 'currency',
          },
          { accessor: 'direction', title: 'Which way', enumGroup: 'roundingDirection' },
          { accessor: 'changeRule', title: 'Change given in', enumGroup: 'roundingChangeRule' },
        ]}
      />
      {editing && (
        <RoundingModal
          key={editing === 'new' ? 'new' : editing.id}
          policy={editing === 'new' ? null : editing}
          spare={spare}
          locationId={locationId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: listKey(ROUNDING_PATH) });
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function RoundingModal({
  policy,
  spare,
  locationId,
  onClose,
  onSaved,
}: {
  policy: RoundingPolicy | null;
  spare: { value: string; label: string }[];
  locationId: string | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [currency, setCurrency] = useState<string | null>(policy?.currency ?? spare[0]?.value ?? null);
  // Shown and typed in whole lempiras or dollars; stored in minor units like every other amount.
  const [increment, setIncrement] = useState<number | string>(policy ? policy.incrementMinor / 100 : 1);
  const [direction, setDirection] = useState<string | null>(policy?.direction ?? 'up');
  const [changeRule, setChangeRule] = useState<string | null>(policy?.changeRule ?? 'sameAsTender');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      rawApi('PUT', ROUNDING_PATH, {
        locationId: policy?.locationId ?? locationId,
        currency,
        incrementMinor: Math.round(Number(increment) * 100),
        direction,
        changeRule,
      }),
    onSuccess: () => {
      notifyDone('Rounding saved', 'Cash totals will be worked out this way from now on.');
      onSaved();
    },
    onError: (err) => notifyError(err, 'save this rounding rule'),
  });

  const submit = () => {
    if (!currency) return setError('Choose which money this is for');
    const amount = Number(increment);
    if (!Number.isFinite(amount) || amount <= 0) {
      return setError('Say what note a total should land on — 1 for a whole lempira');
    }
    setError(null);
    save.mutate();
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={policy ? `Cash rounding in ${enumLabel('currency', policy.currency)}` : 'Set up cash rounding'}
      size="lg"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          This applies once to the whole check, and only when it is paid in cash. It never changes an item
          price, and card payments are unaffected.
        </Text>
        {policy ? (
          <Text size="sm">
            <b>{enumLabel('currency', policy.currency)}</b>
          </Text>
        ) : (
          <Select
            label="Which money"
            required
            data={spare}
            value={currency}
            onChange={setCurrency}
            comboboxProps={{ withinPortal: true }}
          />
        )}
        <NumberInput
          label="Rounds to"
          required
          value={increment}
          onChange={setIncrement}
          min={0}
          decimalScale={2}
          description="The smallest note you can settle in. Put 1 to land on a whole lempira or dollar."
        />
        <Select
          label="Which way"
          required
          data={enumOptions('roundingDirection')}
          value={direction}
          onChange={setDirection}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label="Change given in"
          required
          data={enumOptions('roundingChangeRule')}
          value={changeRule}
          onChange={setChangeRule}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** The tabs, in the order they are shown, so an address can only name one that exists. */
const TABS = ['lists', 'entries', 'preview', 'fx', 'discounts', 'cash'] as const;

export function PricingPage() {
  const locationId = useLocationId();
  /**
   * Which tab you are on, and which price list you are reading, are both in the address.
   *
   * The list is here for the same reason the tab is: without it a price list row could not open
   * its own prices. The Prices tab kept the chosen list in component state, so getting to what
   * Happy Hour prices meant switching tab and then finding "Happy Hour" again in a dropdown,
   * having just clicked the row called Happy Hour.
   */
  const [tab, setTab] = useTabInUrl(TABS, 'lists');
  const [params, setParams] = useSearchParams();
  const chosenList = params.get('list');

  /** The Prices tab, already switched to one list — what a price list row opens. */
  const showPricesOn = (list: string | null) =>
    setParams(list ? { tab: 'entries', list } : { tab: 'entries' }, { replace: true });

  return (
    <>
      <Title order={3} mb="md">
        Prices
      </Title>
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="lists">Price lists</Tabs.Tab>
          <Tabs.Tab value="entries">Prices</Tabs.Tab>
          <Tabs.Tab value="preview">Check a price</Tabs.Tab>
          <Tabs.Tab value="locked">Locked lempira prices</Tabs.Tab>
          <Tabs.Tab value="fx">Exchange rate</Tabs.Tab>
          <Tabs.Tab value="discounts">Discounts</Tabs.Tab>
          <Tabs.Tab value="cash">Cash rounding</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="lists">
          <CrudSection
            title="Price lists"
            entityNoun="price list"
            description="Your everyday prices, plus any specials. When two lists cover the same drink at the same moment, the guest is charged the CHEAPER of them — a happy hour price takes over while it is running because it is lower, not because of any setting."
            listPath="/admin/v1/pricing/price-lists"
            /*
             * THE MENU ONLY. A price agreed with one named regular is stored the same way a menu is,
             * and without this it would sit in this table as an unlabeled row — editable here, where
             * the form offers hours, a channel and a PRIORITY. Setting that priority to 0 is exactly
             * what makes a list the base price the whole bar is charged, so one man's negotiated
             * $6.00 beer is four keystrokes from becoming everybody's.
             *
             * It is not hidden, it is somewhere better: what a person pays is read and changed on
             * that person's own screen, in items and sentences rather than lists and priorities.
             */
            listQuery={{ scope: 'menu' }}
            updatePathTemplate="/admin/v1/pricing/price-lists/{id}"
            defaults={{ locationId }}
            rowLabel={(r) => String(r.name ?? 'this price list')}
            emptyMessage="No price lists yet. Start with your everyday prices, then add specials on top."
            // Edit changes the list's own settings — its hours, its priority — which is not what
            // "open Happy Hour" means to anyone. What is priced ON it was a different tab and a
            // dropdown away, so the list you were already looking at had to be found again.
            rowActions={(row) => (
              <Button size="xs" variant="light" onClick={() => showPricesOn(String(row.id))}>
                Prices on this list
              </Button>
            )}
            columns={[
              { accessor: 'name', title: 'Name' },
              { accessor: 'priority', title: 'Priority' },
              { accessor: 'channelId', title: 'Only for', reference: 'channel' },
              {
                accessor: 'schedule',
                title: 'When',
                // "On a schedule" is not an answer to "when". Whether Happy Hour is set to the
                // right hours is exactly what someone scanning this table wants to see.
                render: (r) => describeSchedule(r.schedule),
              },
              {
                accessor: 'taxInclusive',
                title: 'Tax',
                render: (r) => (r.taxInclusive ? 'Included' : 'Added on top'),
              },
              { accessor: 'active', title: 'In use', render: (r) => (r.active ? 'In use' : 'Off') },
            ]}
            fields={[
              { name: 'name', label: 'Name', type: 'text', required: true },
              {
                name: 'priority',
                label: 'Priority',
                type: 'number',
                required: true,
                description:
                  'Only a tie-breaker. The better price for the guest already wins on its own; this ' +
                  'decides which list is named on the bill when two of them come to exactly the same ' +
                  'amount. Keep your everyday list at zero.',
              },
              {
                name: 'channelId',
                label: 'Only for one sales channel',
                type: 'entity',
                resource: 'channel',
                nullable: true,
                description: 'Leave empty to use these prices everywhere',
              },
              {
                name: 'schedule',
                label: 'When it applies',
                type: 'schedule',
                nullable: true,
                placeholder: 'Always on — these prices apply at every hour of every day.',
                description: 'Happy hour, breakfast, high season. Leave it unset and the list is always on.',
              },
              {
                name: 'taxInclusive',
                label: 'These prices already include tax',
                type: 'boolean',
              },
              { name: 'active', label: 'In use', type: 'boolean', defaultValue: true },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="entries">
          <PriceEntries chosen={chosenList} onChoose={showPricesOn} />
        </Tabs.Panel>

        <Tabs.Panel value="preview">
          <PriceCheck />
        </Tabs.Panel>

        <Tabs.Panel value="locked">
          <LockedLempiraPrices />
        </Tabs.Panel>

        <Tabs.Panel value="fx">
          <CrudSection
            title="Exchange rate"
            entityNoun="rate"
            description="The lempiras-per-dollar rate the registers use. Every rate you enter is kept, so a sale from last month still shows the rate that was in force that day."
            listPath="/admin/v1/pricing/fx-rates"
            defaults={{ locationId }}
            emptyMessage="No rate entered yet. Add today's rate so the registers can take both currencies."
            rowLabel={(r) => `the rate from ${String(r.effectiveAt ?? 'today')}`}
            columns={[
              {
                accessor: 'rateMicro',
                title: 'Lempiras per dollar',
                render: (r) => formatFxRate(r.rateMicro as number),
              },
              { accessor: 'effectiveAt', title: 'In force from', unit: 'datetime' },
              { accessor: 'source', title: 'Entered', enumGroup: 'fxRateSource' },
            ]}
            fields={[
              {
                name: 'rateMicro',
                label: 'Lempiras per dollar',
                type: 'number',
                required: true,
                unit: 'micro',
                description: 'The rate you are giving guests today, for example 26.7',
              },
              {
                name: 'effectiveAt',
                label: 'In force from',
                type: 'datetime',
                required: true,
                description: 'Sales before this moment keep the previous rate',
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="discounts">
          <CrudSection
            title="Discounts"
            entityNoun="discount"
            description="What each discount takes off, whether it can be used alongside another one, and who is allowed to use it."
            listPath="/admin/v1/pricing/discounts"
            updatePathTemplate="/admin/v1/pricing/discounts/{id}"
            defaults={{ locationId }}
            rowLabel={(r) => String(r.nameEn ?? 'this discount')}
            emptyMessage="No discounts set up yet."
            columns={[
              {
                accessor: 'nameEn',
                title: 'Name',
                // A comp is not a discount — it is the house paying — and it was its own column
                // headed "Comp" whose only possible answer was the word "Comp". Said beside the
                // name it is read with the thing it describes, and a column comes back.
                render: (r) => (
                  <div>
                    <Text size="sm">{String(r.nameEn ?? '')}</Text>
                    {Boolean(r.isComp) && (
                      <Badge size="sm" variant="light" color="grape">
                        On the house
                      </Badge>
                    )}
                  </div>
                ),
              },
              { accessor: 'kind', title: 'Kind', enumGroup: 'discountKind' },
              {
                accessor: 'value',
                // The same column is a percentage or an amount, depending on the row's kind — and
                // for a price break it is nothing at all: the register types the price per gesture, so
                // rendering the definition's unused zero as "$0.00" would state a giveaway of
                // nothing where the honest answer is "decided at the register".
                title: 'Takes off',
                render: (r) =>
                  r.kind === 'percent'
                    ? formatRateBp(r.value as number)
                    : r.kind === 'targetPrice'
                      ? 'The difference to the typed price'
                      : formatMoney(r.value as number, (r.fixedCurrency as string) ?? ''),
              },
              { accessor: 'scope', title: 'Applies to', enumGroup: 'discountScope' },
              { accessor: 'stacking', title: 'With others', enumGroup: 'discountStacking' },
              // The conditions were invisible from the list: whether the staff discount is actually
              // restricted to staff is a question this table should answer without opening a row.
              {
                accessor: 'eligibility',
                title: 'Conditions',
                render: (r) => describeEligibility(r.eligibility),
              },
              {
                accessor: 'requiresApproval',
                title: 'Approval',
                /*
                 * THIS COLUMN SAID "A MANAGER", AND NOTHING ANYWHERE MAKES THAT TRUE.
                 *
                 * Not "anyone can apply it" either: giving money away is a permission handed to a
                 * person on the Staff screen, and someone never granted it cannot apply this
                 * discount at all. That part was right. What was wrong is the word for what this
                 * switch does — the old comment here said it "only says whether a second person has
                 * to sign off", and a second person is precisely what it does not get.
                 *
                 * What the register actually does with it: ask for an approving PIN every time instead
                 * of only above the applier's own ceiling, then check that whoever typed it holds
                 * the right to approve this. It never checks that they are somebody else. So on a
                 * shift lead's shift the flag is satisfied by that shift lead's own four digits —
                 * one person, both ends, which is the thing the seeded caps were deliberately
                 * arranged to avoid — and on a bartender's shift, with no approving grant anywhere
                 * on that role, it cannot be satisfied by anybody in the building at two in the
                 * morning and the discount simply cannot be given.
                 *
                 * There is one bartender on a shift here. A rule naming a second, more senior name
                 * is not a stricter rule, it is one nobody can keep — so the column says what is
                 * really being switched, which is WHEN a PIN is asked for, not WHOSE.
                 */
                render: (r) => (r.requiresApproval ? 'Always' : 'Only over their own limit'),
              },
            ]}
            fields={[
              { name: 'nameEn', label: 'Name', type: 'text', required: true },
              { name: 'nameEs', label: 'Name in Spanish', type: 'text', nullable: true },
              {
                name: 'kind',
                label: 'Kind',
                type: 'select',
                required: true,
                options: enumOptions('discountKind'),
              },
              {
                name: 'value',
                label: 'Takes off',
                type: 'number',
                required: true,
                unit: (values) => (values.kind === 'percent' ? 'percent' : 'money'),
                description: 'A percentage, or an amount off, to match the kind chosen above',
              },
              {
                name: 'fixedCurrency',
                label: 'Currency of that amount',
                type: 'select',
                options: enumOptions('currency'),
                nullable: true,
                description: 'Only needed when the discount takes off a fixed amount',
              },
              {
                name: 'basis',
                label: 'Taken off',
                type: 'select',
                options: enumOptions('discountBasis'),
              },
              {
                name: 'stacking',
                label: 'When another discount also applies',
                type: 'select',
                options: enumOptions('discountStacking'),
              },
              {
                name: 'scope',
                label: 'Applies to',
                type: 'select',
                options: enumOptions('discountScope'),
              },
              {
                name: 'eligibility',
                label: 'Who can use it, and when',
                type: 'eligibility',
                description: 'Tick nothing and anyone can use it, at any time',
              },
              {
                name: 'isComp',
                label: 'This is a comp, not a discount',
                type: 'boolean',
                description: 'Comps are reported separately from discounts',
              },
              {
                name: 'requiresApproval',
                label: 'Always ask for an approving PIN',
                type: 'boolean',
                description:
                  'Off, the register only asks when the amount is over what the person applying it may give away. On, it asks every time — and it does not care whose PIN it is, so make sure whoever is on late is allowed to approve this on the Staff screen.',
              },
            ]}
          />

          {/* The staff meal's eligible list, beneath the definitions it belongs to: what "food,
              local beers, basic drinks" MEANS, item by item, failing closed at the register for
              anything unnamed. Its own component because it edits a list, not a row. */}
          <StaffMealEligible />
        </Tabs.Panel>

        <Tabs.Panel value="cash">
          <CashRounding />
          <CrudSection
            title="Notes in the drawer"
            entityNoun="note"
            description="The bills your drawer is counted in. Bills only — no coins circulate on Roatán."
            listPath="/admin/v1/pricing/denominations"
            defaults={{ locationId }}
            emptyMessage="No notes listed yet. Add the bills you actually see in the drawer."
            rowLabel={(r) => `this ${String(r.currency ?? '')} note`}
            columns={[
              { accessor: 'currency', title: 'Money', enumGroup: 'currency' },
              {
                accessor: 'valueMinor',
                title: 'Note',
                unit: 'money',
                currencyAccessor: 'currency',
              },
              // No "In use" column: every note that can be listed here is in use, because nothing
              // can switch one off — it answered its own question on every row and cost the width
              // that the value itself needs.
            ]}
            fields={[
              {
                name: 'currency',
                label: 'Money',
                type: 'select',
                required: true,
                options: enumOptions('currency'),
              },
              { name: 'valueMinor', label: 'Note value', type: 'number', unit: 'money', required: true },
            ]}
          />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}
